/**
 *
 * Copyright 2020-2025 Splunk Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
import { diag } from '@opentelemetry/api';
export const isSessionPersistence = (value) => ['cookie', 'localStorage'].includes(value);
/**
 * Abstract base class for all storage providers.
 * Provides a common interface for different storage mechanisms (cookies, localStorage, sessionStorage, etc.)
  This class implements the Strategy pattern, allowing the StorageManager to work with
 * different storage backends without knowing their specific implementation details.
 */
export class BaseStorageProvider {
    /**
     * Safely parses JSON data from storage with error handling.
     * Returns the parsed object, or undefined if parsing fails or key doesn't exist.
     */
    safelyParseJson(key) {
        const value = this.getValue(key);
        if (!value) {
            return undefined;
        }
        try {
            return JSON.parse(value);
        }
        catch (error) {
            diag.warn(`Failed to parse JSON from ${this.providerName}`, {
                error: error instanceof Error ? error.message : 'Unknown error',
                key,
                value,
            });
            return undefined;
        }
    }
    /**
     * Safely stores an object as JSON in storage with error handling.
     * Returns true if the operation was successful, false otherwise.
     */
    safelyStoreJson(key, data, options) {
        try {
            const serialized = JSON.stringify(data);
            return this.setValue(key, serialized, options);
        }
        catch (error) {
            diag.warn(`Failed to serialize and store JSON in ${this.providerName}`, {
                data,
                error: error instanceof Error ? error.message : 'Unknown error',
                key,
            });
            return false;
        }
    }
}
//# sourceMappingURL=base-storage-provider.js.map